import {useEffect} from 'react'
import {Banner, Link} from '@primer/react'
import type {BannerProps} from '@primer/react'
import type {QueryKey} from '@github-ui/react-query'
import {useAddBanner, useDismissBanner, type InitialBanner} from '@github-ui/banner-stack'
import {useActionsEnable} from '../../hooks/use-actions-enable'
import {useActionsAnalytics} from '../../hooks/use-actions-analytics'

export const ACTIONS_NOT_ENABLED_BANNER_ID = 'actions-not-enabled-banner'

// Banner inputs are typed against Primer's `BannerProps`, which (like all DOM/JSX prop
// types) doesn't formally list `data-*` attributes even though they're valid at
// runtime.
type ActionsNotEnabledBanner = InitialBanner & BannerProps & {'data-test-selector'?: string}

interface ActionsNotEnabledBannerOptions {
  isFork?: boolean
  enablePath: string
  workflowsPath?: string | null
  queryKey: QueryKey
}

function ActionsNotEnabledPrimaryAction({
  isFork = false,
  enablePath,
  queryKey,
}: Pick<ActionsNotEnabledBannerOptions, 'isFork' | 'enablePath' | 'queryKey'>) {
  const enablesAction = useActionsEnable({queryKey, path: enablePath})
  const track = useActionsAnalytics()
  return (
    <Banner.PrimaryAction
      loading={enablesAction.isPending}
      onClick={() => {
        track('workflow_banner.enable_actions', 'ref_loc:actions_not_enabled_banner')
        enablesAction.mutate()
      }}
      // eslint-disable-next-line @github-ui/dotcom-primer/no-title-case
      loadingAnnouncement="Enabling Actions"
    >
      {isFork ? 'I understand my workflows, go ahead and enable them' : 'Enable Actions on this repository'}
    </Banner.PrimaryAction>
  )
}

export function getActionsNotEnabledBanner({
  isFork = false,
  enablePath,
  workflowsPath,
  queryKey,
}: ActionsNotEnabledBannerOptions): ActionsNotEnabledBanner {
  const title = isFork
    ? "Workflows aren't being run on this forked repository"
    : "Workflows aren't being run on this repository"
  const description = isFork ? (
    <>
      Because this repository contained workflow files when it was forked, we have disabled them from running on this
      fork. Make sure you understand the configured workflows and their expected usage before enabling Actions on this
      repository. Some GitHub features that depend on Actions may still be able to run.
      {workflowsPath && (
        <>
          {' '}
          <Link inline href={workflowsPath}>
            View the workflows directory
          </Link>
        </>
      )}
    </>
  ) : (
    <>
      GitHub Actions is not currently enabled on this repository. Some GitHub features that depend on Actions may still
      be able to run.
    </>
  )

  return {
    id: ACTIONS_NOT_ENABLED_BANNER_ID,
    title,
    description,
    variant: 'warning',
    actionsLayout: 'stacked',
    'data-test-selector': ACTIONS_NOT_ENABLED_BANNER_ID,
    primaryAction: <ActionsNotEnabledPrimaryAction isFork={isFork} enablePath={enablePath} queryKey={queryKey} />,
  }
}

export function useActionsNotEnabledBanner({
  showBanner,
  isFork = false,
  enablePath,
  workflowsPath,
  queryKey,
}: ActionsNotEnabledBannerOptions & {
  showBanner: boolean
}) {
  const addBanner = useAddBanner()
  const dismissBanner = useDismissBanner()

  useEffect(() => {
    if (!showBanner) {
      dismissBanner(ACTIONS_NOT_ENABLED_BANNER_ID)
      return
    }

    const id = addBanner(
      getActionsNotEnabledBanner({
        isFork,
        enablePath,
        workflowsPath,
        queryKey,
      }),
    )

    return () => {
      dismissBanner(id)
    }
  }, [showBanner, isFork, enablePath, workflowsPath, queryKey, addBanner, dismissBanner])
}
